// @ts-nocheck
import { supabase } from '@/integrations/supabase/client';
import { formatCurrency } from '@/lib/utils';

export interface BidValidationResult {
  valid: boolean;
  error?: string;
  minimumBid?: number;
  correctedAmount?: number;
  wasRounded: boolean;
  message?: string;
}

export interface BidIncrementRule {
  min_amount: number;
  max_amount: number | null;
  increment: number;
}

/**
 * Gets the applicable bid increment for the given amount with robust error handling
 * Always returns a valid increment - never throws errors
 */
export const getBidIncrementSafe = async (amount: number, auctionId?: string): Promise<number> => {
  try {
    console.log(`🔄 Getting bid increment for amount: $${amount}, auctionId: ${auctionId || 'none'}`);
    
    // First check auction-specific increments
    if (auctionId) {
      const { data: auctionIncrements, error: auctionError } = await supabase
        .from("auction_bid_increments")
        .select("increment")
        .eq("auction_id", auctionId)
        .lte("min_amount", amount)
        .or(`max_amount.is.null,max_amount.gt.${amount}`)  // Changed from gte to gt
        .order("min_amount", { ascending: false })
        .limit(1);

      if (!auctionError && auctionIncrements && auctionIncrements.length > 0) {
        const increment = Number(auctionIncrements[0].increment);
        console.log(`✅ Found auction-specific increment: $${increment} for amount $${amount}`);
        return increment;
      }
    }
    
    // Check global bid increments
    const { data: increments, error } = await supabase
      .from("bid_increments")
      .select("*")
      .lte("min_amount", amount)
      .or(`max_amount.is.null,max_amount.gt.${amount}`)  // Changed from gte to gt
      .order("min_amount", { ascending: false })
      .limit(1);
    
    if (!error && increments && increments.length > 0) {
      const increment = Number(increments[0].increment);
      console.log(`✅ Found global increment: $${increment} for rule: ${JSON.stringify(increments[0])}`);
      return increment;
    }
    
    // Fallback to percentage-based increment if no rules found
    const fallbackIncrement = Math.max(25, Math.round(amount * 0.1));
    console.warn(`⚠️ No bid increment rule found for $${amount}, using fallback: $${fallbackIncrement}`);
    return fallbackIncrement;
    
  } catch (error) {
    console.error("Error getting bid increment:", error);
    // Safe fallback - never let the system break
    const fallbackIncrement = Math.max(25, Math.round(amount * 0.1));
    console.warn(`⚠️ Error occurred, using fallback increment: $${fallbackIncrement}`);
    return fallbackIncrement;
  }
};

/**
 * Validates and corrects a bid amount based on lot state and increment rules
 */
export const validateAndCorrectBid = async (
  lotId: string,
  bidAmount: number
): Promise<BidValidationResult> => {
  try {
    // Get lot details
    const { data: lot, error: lotError } = await supabase
      .from("lots")
      .select("current_bid, starting_bid, auction_id")
      .eq("id", lotId)
      .single();
      
    if (lotError || !lot) {
      return {
        valid: false,
        error: "Could not retrieve lot information",
        wasRounded: false
      };
    }
    
    const startingBid = Number(lot.starting_bid) || 0;
    const currentBid = lot.current_bid ? Number(lot.current_bid) : null;
    const auctionId = lot.auction_id;
    
    console.log(`🎯 Validating bid: $${bidAmount} | Starting: $${startingBid} | Current: ${currentBid ? `$${currentBid}` : 'none'}`);
    
    // Check if there are actual bids in the database
    const { data: platformBids } = await supabase
      .from('bids')
      .select('id')
      .eq('lot_id', lotId)
      .eq('status', 'approved')
      .limit(1);
      
    const { data: manualBids } = await supabase
      .from('manual_bids')
      .select('id')
      .eq('lot_id', lotId)
      .limit(1);
      
    const hasActualBids = (platformBids && platformBids.length > 0) || (manualBids && manualBids.length > 0);
    
    // Case 1: No current bid exists OR current_bid equals starting_bid with no actual bids
    // In this case, bids must follow increments starting from starting bid
    if (!currentBid || currentBid < startingBid || (currentBid === startingBid && !hasActualBids)) {
      if (bidAmount < startingBid) {
        return {
          valid: false,
          error: `Minimum bid is ${formatCurrency(startingBid)}`,
          minimumBid: startingBid,
          wasRounded: false
        };
      }

      const increment = await getBidIncrementSafe(startingBid, auctionId);
      if (!increment) {
        console.error(`❌ No increment rule found for starting bid $${startingBid}. Please review bid increments.`);
        return {
          valid: bidAmount === startingBid,
          wasRounded: false,
          correctedAmount: bidAmount === startingBid ? undefined : startingBid,
          message: bidAmount === startingBid ? undefined : `Rounded to ${formatCurrency(startingBid)}`,
        };
      }

      if (bidAmount === startingBid) {
        console.log(`✅ First bid equals starting bid (${formatCurrency(startingBid)})`);
        return {
          valid: true,
          wasRounded: false
        };
      }

      const difference = bidAmount - startingBid;
      if (difference % increment === 0) {
        console.log(`✅ First bid follows increment rules (difference ${difference}, increment ${increment})`);
        return {
          valid: true,
          wasRounded: false
        };
      }

      const incrementsNeeded = Math.ceil(difference / increment);
      const correctedAmount = startingBid + (incrementsNeeded * increment);
      console.warn(`⚠️ First bid adjusted to increment grid. Starting: $${startingBid}, increment: $${increment}, corrected: $${correctedAmount}`);

      return {
        valid: true,
        correctedAmount,
        wasRounded: true,
        message: `Rounded to ${formatCurrency(correctedAmount)} (increment: ${formatCurrency(increment)})`
      };
    }
    
    // Case 2: Current bid exists and has actual bids - must be HIGHER (not equal!)
    // CRITICAL: Prevent duplicate bids at same amount
    if (bidAmount <= currentBid) {
      const increment = await getBidIncrementSafe(currentBid, auctionId);
      const minimumNextBid = currentBid + increment;
      
      return {
        valid: false,
        error: `Bid must be HIGHER than current bid of ${formatCurrency(currentBid)}. Someone else already bid this amount!`,
        minimumBid: minimumNextBid,
        correctedAmount: minimumNextBid,
        wasRounded: true,
        message: `Minimum next bid is ${formatCurrency(minimumNextBid)}`
      };
    }
    
    // Case 3: Bid is higher than current - check if it follows increment rules
    const difference = bidAmount - currentBid;
    const increment = await getBidIncrementSafe(currentBid, auctionId);
    
    console.log(`💰 Checking increment: difference=$${difference}, increment=$${increment}`);
    
    // Check if bid follows exact increment rules
    if (difference % increment === 0) {
      console.log(`✅ Bid follows increment rules exactly`);
      return {
        valid: true,
        wasRounded: false
      };
    }
    
    // Round up to next valid increment
    const incrementsNeeded = Math.ceil(difference / increment);
    const correctedAmount = currentBid + (incrementsNeeded * increment);
    
    console.log(`🔄 Rounding: ${incrementsNeeded} increments needed, corrected to $${correctedAmount}`);
    
    return {
      valid: true,
      correctedAmount,
      wasRounded: true,
      message: `Rounded to ${formatCurrency(correctedAmount)} (increment: ${formatCurrency(increment)})`
    };
    
  } catch (error) {
    console.error("Error in validateAndCorrectBid:", error);
    return {
      valid: false,
      error: "Error validating bid",
      wasRounded: false
    };
  }
};

/**
 * Gets the next minimum bid amount for a lot
 */
export const getNextMinimumBid = async (lotId: string): Promise<number> => {
  try {
    const { data: lot } = await supabase
      .from('lots')
      .select('current_bid, starting_bid, auction_id')
      .eq('id', lotId)
      .single();

    if (!lot) return 0;

    const startingBid = Number(lot.starting_bid) || 0;
    const currentBid = lot.current_bid ? Number(lot.current_bid) : null;
    const auctionId = lot.auction_id;

    // If no current bid, minimum is starting bid
    if (!currentBid || currentBid < startingBid) {
      return startingBid;
    }

    // Current bid exists - add increment
    const increment = await getBidIncrementSafe(currentBid, auctionId);
    return currentBid + increment;
    
  } catch (error) {
    console.error('Error getting minimum bid:', error);
    return 0;
  }
};